import React, { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  Card, CardBody, Col, Row, Nav, NavItem, NavLink, TabContent, TabPane, Table,
  Button, Modal, ModalHeader, ModalBody, ModalFooter, Input, Label, FormGroup,
} from "reactstrap"
import FeatherIcon from "feather-icons-react"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import { toastError, toastSuccess } from "../../helpers/msg_helper"
import "./tenancy.css"
import {
  getTenantCompanies, toggleTenantCompany,
  getCompanyGroups, createCompanyGroup, updateCompanyGroup, deleteCompanyGroup,
  getGroupSettings, saveGroupSettings,
} from "../../services/tenancy"

const emptyGroup = { code: "", name: "", is_active: true, member_ids: [], primary_company_id: "" }

// Tally-style Accounting Features (F11) — render on the "Accounting" tab, in order.
const ACCOUNTING_KEYS = [
  "finance.bill_wise_details", "finance.cost_center_enabled", "finance.multiple_cost_category",
  "finance.interest_calculation", "finance.vat_enabled", "finance.vat_rate",
]

const Tenancy = () => {
  document.title = "Company Management | Everrise"
  const navigate = useNavigate()
  const [tab, setTab] = useState("companies")
  const [companies, setCompanies] = useState([])
  const [groups, setGroups] = useState([])
  const [reload, setReload] = useState(0)

  const [groupModal, setGroupModal] = useState(false)
  const [group, setGroup] = useState(emptyGroup)
  const [settingsModal, setSettingsModal] = useState(false)
  const [settingsScope, setSettingsScope] = useState(null) // {type:'group', id, name}
  const [settingsRows, setSettingsRows] = useState([])
  const [settingsTab, setSettingsTab] = useState("accounting")

  useEffect(() => {
    ;(async () => {
      try {
        const c = await getTenantCompanies()
        setCompanies(c?.data || [])
        const g = await getCompanyGroups()
        setGroups(g?.data || [])
      } catch (e) {
        toastError(e?.message || "Failed to load")
      }
    })()
  }, [reload])

  const toggleCompany = async id => {
    try { await toggleTenantCompany(id); setReload(reload + 1) } catch (e) { toastError(e?.message) }
  }

  // ---- Group form ----
  const openGroup = item => {
    setGroup(item
      ? { ...emptyGroup, ...item, member_ids: (item.companies || companies.filter(c => c.group_id === item.id)).map(c => c.id), primary_company_id: item.primary_company_id || "" }
      : emptyGroup)
    setGroupModal(true)
  }
  const saveGroup = async () => {
    try {
      const payload = { ...group, primary_company_id: group.primary_company_id || null }
      if (group.id) await updateCompanyGroup(group.id, payload)
      else await createCompanyGroup(payload)
      toastSuccess("Group saved")
      setGroupModal(false)
      setReload(reload + 1)
    } catch (e) {
      toastError(e?.message || (e?.errors && Object.values(e.errors)[0][0]) || "Save failed")
    }
  }
  const removeGroup = async id => {
    if (!window.confirm("Delete this group? Member companies become standalone.")) return
    try { await deleteCompanyGroup(id); toastSuccess("Group deleted"); setReload(reload + 1) } catch (e) { toastError(e?.message) }
  }
  const toggleMember = id => {
    setGroup(g => ({ ...g, member_ids: g.member_ids.includes(id) ? g.member_ids.filter(x => x !== id) : [...g.member_ids, id] }))
  }

  // ---- Group settings editor ----
  const openGroupSettings = async item => {
    try {
      const res = await getGroupSettings(item.id)
      setSettingsRows((res?.settings || []).map(s => ({ ...s, input: s.own_value === null || s.own_value === undefined ? "" : String(s.own_value) })))
      setSettingsScope({ type: "group", id: item.id, name: item.name })
      setSettingsTab("accounting")
      setSettingsModal(true)
    } catch (e) {
      toastError(e?.message || "Failed to load settings")
    }
  }
  const setSettingInput = (key, val) => setSettingsRows(rows => rows.map(r => (r.key === key ? { ...r, input: val } : r)))
  const saveSettings = async () => {
    try {
      const payload = {}
      settingsRows.forEach(r => { payload[r.key] = r.input })
      await saveGroupSettings(settingsScope.id, payload)
      toastSuccess("Settings saved")
      setSettingsModal(false)
    } catch (e) {
      toastError(e?.message || "Save failed")
    }
  }

  const groupName = id => groups.find(g => g.id === id)?.name || "—"

  // Companies table used inside each group card / the standalone card.
  const renderCompanyTable = (list, g) => (
    <Table responsive borderless className="align-middle tnt-table mb-0">
      <thead>
        <tr>
          <th>Code</th><th>Name</th><th>MOL</th><th>Capabilities</th>
          <th className="text-end">Camps</th><th className="text-end">Projects</th>
          <th>Status</th><th className="text-end">Actions</th>
        </tr>
      </thead>
      <tbody>
        {list.map(c => (
          <tr key={c.id}>
            <td className="tnt-cg-code">{c.code}</td>
            <td>
              <span className="fw-500">{c.company_name}</span>
              {g && g.primary_company_id === c.id && <span className="tnt-primary">primary</span>}
            </td>
            <td className="text-muted">{c.mol_number || "—"}</td>
            <td>
              {c.is_payroll && <span className="tnt-cap">Payroll</span>}
              {c.is_billing && <span className="tnt-cap">Billing</span>}
              {c.is_vehicle_owner && <span className="tnt-cap">Vehicles</span>}
              {!c.is_payroll && !c.is_billing && !c.is_vehicle_owner && <span className="text-muted">—</span>}
            </td>
            <td className="text-end">{c.camps_count ?? "—"}</td>
            <td className="text-end">{c.projects_count ?? "—"}</td>
            <td>{c.is_active
              ? <span className="tnt-pill tnt-pill-active">active</span>
              : <span className="tnt-pill tnt-pill-inactive">inactive</span>}</td>
            <td className="text-end" style={{ whiteSpace: "nowrap" }}>
              <i className="uil-pen action-icon" role="button" title="Edit" onClick={() => navigate(`/company-management/companies/${c.id}`)} />
              <i className={`${c.is_active ? "uil-ban" : "uil-check-circle"} action-icon`} role="button" title={c.is_active ? "Deactivate" : "Activate"} onClick={() => toggleCompany(c.id)} />
            </td>
          </tr>
        ))}
        {!list.length && <tr><td colSpan="8" className="text-center text-muted py-3">No companies</td></tr>}
      </tbody>
    </Table>
  )

  const renderSettingRow = r => (
    <Row key={r.key} className="align-items-center mb-2">
      <Col md={6}><Label className="mb-0">{r.label}{r.unit ? ` (${r.unit})` : ""}</Label>
        {r.help && <div className="text-muted small">{r.help}</div>}
      </Col>
      <Col md={6}>
        {r.type === "boolean" ? (
          <Input type="select" value={r.input} onChange={e => setSettingInput(r.key, e.target.value)}>
            <option value="">Inherit ({String(r.inherited_value)})</option>
            <option value="1">Yes</option>
            <option value="0">No</option>
          </Input>
        ) : r.type === "select" ? (
          <Input type="select" value={r.input} onChange={e => setSettingInput(r.key, e.target.value)}>
            <option value="">Inherit ({String(r.inherited_value)})</option>
            {(r.options || []).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Input>
        ) : (
          <Input value={r.input} placeholder={`Inherit (${String(r.inherited_value)})`} onChange={e => setSettingInput(r.key, e.target.value)} />
        )}
      </Col>
    </Row>
  )

  return (
    <div className="page-content tnt-scope">
      <Breadcrumbs title="Administration" breadcrumbObject={[{ title: "Administration", link: "#" }, { title: "Group / Company", link: "#" }]} />

      {/* Summary + actions on the page body (grey) */}
      <div className="d-flex justify-content-between align-items-center flex-wrap mb-3 px-1">
        <div className="text-muted">
          {companies.length} companies · {groups.length} group(s). A company can be in a group or standalone.
        </div>
        <div>
          <Button color="light" className="tnt-btn-outline me-2" onClick={() => openGroup(null)}>+ New group</Button>
          <Button color="primary" onClick={() => navigate("/company-management/companies/new")}>+ New company</Button>
        </div>
      </div>

      {/* One white card per group */}
      {groups.map(g => {
        const members = companies.filter(c => c.group_id === g.id)
        return (
          <Card className="me-2 mb-3 main-dashboard-bg tnt-cg-card" key={g.id}>
            <CardBody>
              <div className="d-flex justify-content-between align-items-start flex-wrap mb-3">
                <div className="d-flex align-items-center">
                  <div className="tnt-cg-logo">{g.logo ? <img src={g.logo} alt="" /> : "no logo"}</div>
                  <div className="ms-3">
                    <div className="tnt-cg-gname">{g.name}<span className="tnt-cg-gcode">{g.code}</span></div>
                    <div className="text-muted small">{members.length} companies in this group</div>
                  </div>
                </div>
                <div className="d-flex">
                  <Button color="light" className="tnt-btn-outline me-2" onClick={() => openGroupSettings(g)}>Settings</Button>
                  <Button color="light" className="tnt-btn-outline" onClick={() => openGroup(g)}>Edit group</Button>
                </div>
              </div>
              {renderCompanyTable(members, g)}
            </CardBody>
          </Card>
        )
      })}

      {/* Standalone companies */}
      {companies.some(c => !c.group_id) && (
        <Card className="me-2 mb-3 main-dashboard-bg tnt-cg-card">
          <CardBody>
            <div className="tnt-cg-gname mb-3">Standalone companies</div>
            {renderCompanyTable(companies.filter(c => !c.group_id), null)}
          </CardBody>
        </Card>
      )}

      {!companies.length && !groups.length && (
        <Card className="me-2 main-dashboard-bg tnt-cg-card">
          <CardBody><div className="text-center text-muted py-4">No companies or groups yet.</div></CardBody>
        </Card>
      )}

      {/* GROUP MODAL */}
      <Modal isOpen={groupModal} toggle={() => setGroupModal(false)}>
        <ModalHeader toggle={() => setGroupModal(false)}>{group.id ? "Edit Group" : "New Group"}</ModalHeader>
        <ModalBody>
          <Row>
            <Col md={6}><FormGroup><Label>Code *</Label><Input value={group.code} onChange={e => setGroup({ ...group, code: e.target.value })} /></FormGroup></Col>
            <Col md={6}><FormGroup><Label>Name *</Label><Input value={group.name} onChange={e => setGroup({ ...group, name: e.target.value })} /></FormGroup></Col>
          </Row>
          <FormGroup>
            <Label>Member companies</Label>
            <div style={{ maxHeight: 160, overflow: "auto", border: "1px solid #eee", padding: 8, borderRadius: 6 }}>
              {companies.map(c => (
                <FormGroup check key={c.id}>
                  <Input id={`mem_${c.id}`} type="checkbox" checked={group.member_ids.includes(c.id)} onChange={() => toggleMember(c.id)} />{" "}
                  <Label check for={`mem_${c.id}`} style={{ cursor: "pointer" }}>{c.company_name} ({c.code})</Label>
                </FormGroup>
              ))}
            </div>
          </FormGroup>
          <FormGroup>
            <Label>Primary company</Label>
            <Input type="select" value={group.primary_company_id} onChange={e => setGroup({ ...group, primary_company_id: e.target.value })}>
              <option value="">—</option>
              {companies.filter(c => group.member_ids.includes(c.id)).map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
            </Input>
          </FormGroup>
        </ModalBody>
        <ModalFooter className="justify-content-between">
          <div>{group.id && <Button color="link" className="text-danger p-0" onClick={() => { setGroupModal(false); removeGroup(group.id) }}>Delete group</Button>}</div>
          <div>
            <Button color="light" className="me-2" onClick={() => setGroupModal(false)}>Cancel</Button>
            <Button color="primary" onClick={saveGroup}>Save</Button>
          </div>
        </ModalFooter>
      </Modal>

      {/* GROUP SETTINGS MODAL */}
      <Modal isOpen={settingsModal} toggle={() => setSettingsModal(false)} size="lg">
        <ModalHeader toggle={() => setSettingsModal(false)}>{settingsScope?.name} — Settings (group)</ModalHeader>
        <ModalBody>
          <Nav tabs className="mb-3">
            <NavItem><NavLink className={settingsTab === "accounting" ? "active" : ""} style={{ cursor: "pointer" }} onClick={() => setSettingsTab("accounting")}>Accounting Features</NavLink></NavItem>
            <NavItem><NavLink className={settingsTab === "other" ? "active" : ""} style={{ cursor: "pointer" }} onClick={() => setSettingsTab("other")}>Other Settings</NavLink></NavItem>
          </Nav>
          <p className="text-muted small">Group-level defaults. Leave a field blank to inherit from global. Member companies inherit these unless overridden.</p>
          <TabContent activeTab={settingsTab}>
            <TabPane tabId="accounting">{ACCOUNTING_KEYS.map(k => settingsRows.find(r => r.key === k)).filter(Boolean).map(renderSettingRow)}</TabPane>
            <TabPane tabId="other">{settingsRows.filter(r => !ACCOUNTING_KEYS.includes(r.key)).map(renderSettingRow)}</TabPane>
          </TabContent>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setSettingsModal(false)}>Cancel</Button>
          <Button color="primary" onClick={saveSettings}>Save</Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default Tenancy
